import { useTranslation } from 'react-i18next';
import { KoobButtonMenu, KoobMenuItem } from '@koob/ui';

export interface FolderAddServiceMenuProps {
  onAddHotel: () => void;
  onAddExperience: () => void;
  disabled?: boolean;
}

// KoobMenuItem greys its icons: `!` keeps the tile colours of the mockup.
const ServiceOption = ({
  icon,
  tileClass,
  title,
  hint,
}: {
  icon: string;
  tileClass: string;
  title: string;
  hint: string;
}) => (
  <span className="flex items-start gap-3 py-0.5">
    <span
      className={`flex h-8 w-8 flex-none items-center justify-center rounded-lg ${tileClass}`}
    >
      <i className={`${icon} text-sm`} aria-hidden="true" />
    </span>
    <span className="flex flex-col gap-0.5">
      <span className="font-bold text-gray-900">{title}</span>
      <span className="text-xs font-normal text-gray-500">{hint}</span>
    </span>
  </span>
);

/**
 * KD-3842 "Add a service": Hotel or Experience, both added as standalone
 * bookings (Design a trip combines them).
 */
export default function FolderAddServiceMenu({
  onAddHotel,
  onAddExperience,
  disabled = false,
}: FolderAddServiceMenuProps) {
  const { t } = useTranslation('folders');

  return (
    <KoobButtonMenu
      variant="brandOutline"
      label={t('actions.addService')}
      icon="fa-regular fa-plus"
      caret
      disabled={disabled}
      testId="folder-add-service"
      menu={
        <div className="w-[300px]">
          <KoobMenuItem onClick={onAddHotel}>
            <ServiceOption
              icon="fa-regular fa-bed"
              tileClass="bg-blue-100 [&_i]:!text-blue-800"
              title={t('actions.addHotel')}
              hint={t('actions.addHotelHint')}
            />
          </KoobMenuItem>
          <KoobMenuItem onClick={onAddExperience}>
            <ServiceOption
              icon="fa-regular fa-layer-group"
              tileClass="bg-violet-100 [&_i]:!text-violet-700"
              title={t('actions.addExperience')}
              hint={t('actions.addExperienceHint')}
            />
          </KoobMenuItem>
          <div className="mx-1.5 my-1 h-px bg-gray-100" />
          <p className="m-0 px-3 pb-2 pt-1.5 text-xs text-gray-400">
            {t('actions.addServiceNote')}
          </p>
        </div>
      }
    />
  );
}
